import { Link } from "@inertiajs/react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Spinner, ProgressBar } from "@/components/ui/progress"
import { NewBlankSheetDialog } from "@/components/plan-sets/NewBlankSheetDialog"
import { UploadPlanSetsDialog } from "@/components/plan-sets/UploadPlanSetsDialog"
import {
  PROCESSING_LABELS,
  PROCESSING_TONES,
  renderTarget,
  renderedOfTarget,
  type PlanSetRow,
} from "@/lib/plan-set-status"

export function PlanSetsSection({ projectId, planSets }: { projectId: number; planSets: PlanSetRow[] }) {
  return (
    <section className="mt-10">
      <div className="flex items-center justify-between gap-4">
        <h2>Plan sets</h2>
        <span className="flex gap-2">
          <NewBlankSheetDialog projectId={projectId} />
          <UploadPlanSetsDialog postUrl={`/projects/${projectId}/plan_sets`} />
        </span>
      </div>

      {planSets.length === 0 ? (
        <div className="callout mt-4">
          <p>
            No plan sets yet. Upload the builder&apos;s PDF plan set and promote
            the pages worth measuring.
          </p>
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-hairline overflow-hidden rounded-lg border border-hairline bg-page">
          {planSets.map((planSet) => (
            <li key={planSet.id} className="space-y-3 px-4 py-4">
              <div className="flex flex-wrap items-center gap-3">
                <Link href={planSet.picker_url} className="font-medium text-ink-display no-underline hover:underline">
                  {planSet.title}
                </Link>
                <Badge tone={PROCESSING_TONES[planSet.status]}>
                  {PROCESSING_LABELS[planSet.status]}
                </Badge>
                {planSet.page_count != null && planSet.status === "ready" && (
                  <span className="text-xs text-ink-muted">
                    {planSet.page_count} pages · uploaded by {planSet.created_by}
                  </span>
                )}
                <span className="ml-auto flex items-center gap-2">
                  <Button asChild variant="ghost" size="sm">
                    <a href={planSet.download_url}>Download PDF</a>
                  </Button>
                  <Button asChild variant="secondary" size="sm">
                    <Link href={planSet.picker_url}>Pick pages</Link>
                  </Button>
                </span>
              </div>

              {(planSet.status === "queued" || planSet.status === "processing") && (
                <div className="flex items-center gap-3">
                  <Spinner />
                  <div className="max-w-xs flex-1 space-y-1">
                    <ProgressBar
                      value={renderTarget(planSet) ? (renderedOfTarget(planSet) / renderTarget(planSet)) * 100 : 0}
                    />
                    <p className="text-xs text-ink-muted">
                      {planSet.page_count
                        ? `${renderedOfTarget(planSet)} / ${renderTarget(planSet)} pages`
                        : "Reading the PDF…"}
                    </p>
                  </div>
                </div>
              )}
              {planSet.status === "failed" && (
                <p className="text-xs text-danger-display">{planSet.error_message}</p>
              )}

              {planSet.sheets.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  {planSet.sheets.map((sheet) => (
                    <span key={sheet.id} className="inline-flex items-center gap-2 rounded-full border border-hairline bg-surface px-3 py-1 text-sm">
                      {sheet.status === "ready" ? (
                        <Link href={sheet.editor_url} className="font-medium text-ink-display no-underline hover:underline">
                          {sheet.label}
                        </Link>
                      ) : (
                        <span className="font-medium text-ink-display">{sheet.label}</span>
                      )}
                      <span className="text-xs text-ink-muted">p{sheet.page_number}</span>
                      {sheet.status === "ready" ? (
                        <Badge tone="neutral">Ready</Badge>
                      ) : sheet.status === "failed" ? (
                        <Badge tone="signal">Failed</Badge>
                      ) : (
                        <Spinner />
                      )}
                    </span>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
